import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { toastConstant } from 'constants/toastmessage';
import { useToast } from 'context/ToastContext';
import { useToggle } from 'hooks/useToggle';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { AutoComplete } from 'primereact/autocomplete';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import { InputNumber } from 'primereact/inputnumber';
import { useEffect, useState, useCallback } from 'react';
import { Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { updateLeadDetails } from 'services/leadServices';
import { UpdateContactDialog } from './UpdateContactDialog';
import { updateCaseContactV2 } from 'services/CaseServices';
import AddInsurerDialog from 'components/AddInsurerDialog/AddInsurerDialog';

import 'assets/scss/style.scss';
import { useContactList } from 'hooks/useContactList';
import { useDebounce } from 'hooks/useDebounce';
import { formatDateWithoutTimezone } from 'utils/utils';
import { parseDOB } from 'modules/lead/helpers/helpers';
import { createInsurer, updateInsurer } from 'services/insuranceProviderService';
import { getExcludedLawftIdsForInsurer } from 'utils/lawftIdListHelpers';
import AsyncPaginate from 'components/AsyncPaginate/AsyncPaginate';
import { getGlobalDropdown } from 'services/caseLeadDropdownService';

const ClientInformation = ({
  control,
  caseDetails,
  watch,
  setValue,
  isLeadSpecific,
  handleSubmit,
  fetchLeadDetails,
  getCaseDetails,
  userDetails,
}) => {
  const [age, setAge] = useState();
  const { addToast } = useToast();
  const [isFullNamedropdown, setToggleFullName] = useToggle(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [insurerOptions, setInsurerOptions] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);
  const [showAddInsurerDialog, setShowAddInsurerDialog] = useState(false);
  const [selectedInsurer, setSelectedInsurer] = useState(null);
  const [selectedLawftIdList, setSelectedLawftIdList] = useState({});

  const [searchQuery, setSearchQuery] = useState('');

  const debouncedQuery = useDebounce(searchQuery, 300);

  const insuranceCompanyValue = caseDetails?.client_details?.insurance_company;

  const clientDetails = caseDetails?.client_details?.[0];
  const { searchContactsWithFullObjects, contactsList } = useContactList({
    isCase: !isLeadSpecific,
    isLeads: isLeadSpecific,
  });

  // Load insurer list from insurance provider API
  const loadInsurers = useCallback(async () => {
    if (!caseDetails?.case_id) return;

    try {
      const response = await getGlobalDropdown('', 'insurer', '', caseDetails?.case_id, false, 1, 15, '', '', insuranceCompanyValue);
      // Handle different possible response structures
      const insurers = response?.data?.data || [];

      // Show all insurers without filtering by client_insurer flag
      // Store full insurers list
      setInsuranceProviders(insurers);

      const options = insurers.map((insurer) => {
        const insuredPerson = insurer?.insured_person;
        let insuredPersonName = '';
        if (insuredPerson?.first_name || insuredPerson?.last_name) {
          insuredPersonName = `${insuredPerson?.first_name || ''} ${insuredPerson?.last_name || ''}`.trim();
        } else if (insuredPerson?.title) {
          insuredPersonName = insuredPerson.title;
        }
        const label = insuredPersonName
          ? `${insurer?.insurer_name || 'Untitled'} (${insuredPersonName})`
          : insurer?.insurer_name || 'Untitled';

        return {
          label: label,
          value: insurer?.insurer_id, // Use insurer_id as value for uniqueness
          insurer_id: insurer?.insurer_id,
          insurer_name: insurer?.insurer_name || 'Untitled',
          insurer_pk: insurer?.pk,
          insurer_sk: insurer?.sk,
        };
      });
      setInsurerOptions(options);
    } catch (error) {
      console.error('Error loading insurers:', error);
      setInsuranceProviders([]);
      setInsurerOptions([]); // Set empty array on error
    }
  }, [caseDetails?.case_id]);

  // Load insurers on mount and when case details change
  useEffect(() => {
    loadInsurers();
  }, [loadInsurers, caseDetails?.insurance_details]);

  // Track selected Lawft IDs from all contacts
  useEffect(() => {
    if (!caseDetails?.case_id) return;

    // Track selected Lawft IDs from all contacts
    const selectedLawftId = {};

    // Get Lawft IDs from relations (Person and Business contacts)
    caseDetails?.relations?.forEach((relation) => {
      if (relation?.lawft_id && relation?.contact_id) {
        selectedLawftId[relation.contact_id] = relation.lawft_id;
      }
    });

    // Get Lawft IDs from client_details
    caseDetails?.client_details?.forEach((clientDetail) => {
      if (clientDetail?.lawft_id && clientDetail?.contact_id) {
        selectedLawftId[clientDetail.contact_id] = clientDetail.lawft_id;
      }
    });

    // Get Lawft IDs from defendants
    caseDetails?.defendant?.forEach((defendant) => {
      if (defendant?.lawft_id && defendant?.contact_id) {
        selectedLawftId[defendant.contact_id] = defendant.lawft_id;
      }
    });

    setSelectedLawftIdList(selectedLawftId);
  }, [
    caseDetails?.case_id,
    caseDetails?.case_description?.case_practice_area,
    caseDetails?.relations,
    caseDetails?.client_details,
    caseDetails?.defendant,
  ]);

  // Handle Add Insurer Submit
  const onSubmitInsurer = async (data) => {
    try {
      // Get client contact_id
      const clientContactId = clientDetails?.contact_id;

      // Check if an insurance provider already exists for this client
      const existingProvider = caseDetails?.insurance_details?.find(
        (provider) =>
          provider?.client_insurer === true &&
          (provider?.insured_person?.contact_id === clientContactId || provider?.insured_contact_id === clientContactId)
      );

      // Map form data to API structure
      const insurerData = {
        case_id: caseDetails?.case_id,
        insurer_name: data.insurance_company,
        insured_person: clientContactId ? { contact_id: clientContactId } : null,
        contact_name: data.contact_name,
        email: data.email,
        phone_number: data.phone,
        additional_phone: data.additional_phone || '',
        fax: data.fax || '',
        policy_number: data.policy_number,
        claim_number: data.claim_number || '',
        insurance_limit: parseFloat(data.insurance_limit) || 0,
        lawft_id: data.lawft_id || [], // Add lawft_id array
        client_insurer: true, // Mark this as a client insurer
        defendant_insurer: false,
      };

      let response;
      if (existingProvider) {
        // Update existing insurance provider instead of creating a new one
        const updateData = {
          ...insurerData,
          insurer_pk: existingProvider.pk,
          insurer_sk: existingProvider.sk,
          insurer_id: existingProvider.insurer_id,
        };
        response = await updateInsurer(updateData);
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider updated successfully');
      } else {
        // Create new insurance provider
        response = await createInsurer(insurerData);
        addToast(false, toastConstant.toasterType.SUCCESS, 'Success', 'Insurance provider added successfully');
      }

      if (response?.data?.insurer_id || response?.data?.message || response?.data) {
        const newInsurerId = response?.data?.insurer_id || existingProvider?.insurer_id;

        // Reload insurers list to get the updated insurer
        await loadInsurers();

        // Reload case details to refresh insurance_details
        try {
          await getCaseDetails();
        } catch (e) {
          console.error(e);
        }

        // Set the newly created/updated insurer as selected after a short delay to ensure options are updated
        setTimeout(() => {
          if (newInsurerId) {
            setValue('client_details.insurer', newInsurerId);
          }
        }, 100);

        setShowAddInsurerDialog(false);
      }
    } catch (error) {
      console.error('Error adding insurer:', error);
      addToast(false, toastConstant.toasterType.ERROR, 'Error', error?.response?.data?.body || 'Failed to add/update insurance provider');
    }
  };

  // Search function for AutoComplete
  const searchContact = useCallback(async (event) => {
    const query = event.query;

    setSearchQuery(query);
  }, []);

  // Effect to handle debounced search
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedQuery && debouncedQuery.length > 0) {
        const results = await searchContactsWithFullObjects(debouncedQuery);

        setSuggestions(results);
      } else {
        setSuggestions([]);
      }
    };

    performSearch();
  }, [debouncedQuery, searchContactsWithFullObjects]);

  const onSelectContact = (e) => {
    if (e.value && e.value.value) {
      setSelectedContact(e.value.client); // Store the full contact object
    }
  };

  const handlePopupConfirmation = () => {
    setShowUpdateDialog(true);
  };

  const handleDialogHide = () => {
    setShowUpdateDialog(false);
  };

  const handleRetain = () => {
    handleSubmit((data) => handleClientInformationSubmit(data, true))();
    setShowUpdateDialog(false);
    setToggleFullName(false);
  };

  const handleReset = () => {
    handleSubmit((data) => handleClientInformationSubmit(data, false))();
    setShowUpdateDialog(false);
    setToggleFullName(false);
  };

  const handleClientInformationSubmit = async (data, isRetain = false) => {
    const contact = selectedContact;

    let updatedClientData = {
      vehicle_info: isRetain ? data?.client_details?.vehicle_info : '',
      medical_summary: isRetain ? data?.client_details?.medical_summary : '',
      past_injuries: isRetain ? data?.client_details?.past_injuries : '',
      collision_limit: isRetain ? data?.client_details?.collision_limit : 0,
      comprehensive_limit: isRetain ? data?.client_details?.comprehensive_limit : 0,
      pip_limit: isRetain ? data?.client_details?.pip_limit : 0,
      medpay_limit: isRetain ? data?.client_details?.medpay_limit : 0,
    };

    // Add insurer and insurance_company if isRetain is true - check multiple sources
    if (isRetain) {
      const insurerId = data?.client_details?.insurer; // Now stores insurer_id
      if (selectedInsurer) {
        updatedClientData.insurer = selectedInsurer?.insurer_id;
        updatedClientData.insurance_company = selectedInsurer?.insurer_name;
      } else if (insurerId) {
        // If insurer ID exists in form data but not in selectedInsurer, try to find from insurerOptions
        const matchedInsurer = insurerOptions?.find((opt) => opt.insurer_id === insurerId);
        if (matchedInsurer) {
          updatedClientData.insurer = matchedInsurer?.insurer_id;
          updatedClientData.insurance_company = matchedInsurer?.insurer_name;
        }
      } else {
        // If no insurer selected and no insurerId, clear the insurer fields
        updatedClientData.insurer = '';
        updatedClientData.insurance_company = '';
      }
    }

    const updatedData = { ...contact, role: undefined, ...updatedClientData };

    if (!isLeadSpecific) {
      await updateCaseContactV2(caseDetails?.case_id, updatedData, clientDetails?.sk);
      await getCaseDetails();

      // Explicitly clear form fields if this is a reset operation
      if (!isRetain) {
        setValue('client_details.vehicle_info', '');
        setValue('client_details.medical_summary', '');
        setValue('client_details.past_injuries', '');
        setValue('client_details.insurer', '');
        setValue('client_details.collision_limit', 0);
        setValue('client_details.comprehensive_limit', 0);
        setValue('client_details.pip_limit', 0);
        setValue('client_details.medpay_limit', 0);
      }

      setToggleFullName(false);
    } else {
      updateLeadDetails(data.sk, data)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
          fetchLeadDetails();
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
        });
    }
  };

  const handleClientInformationUpdate = async (data, isRetain = false) => {
    let updatedClientData = {
      vehicle_info: data?.client_details?.vehicle_info,
      medical_summary: data?.client_details?.medical_summary,
      past_injuries: data?.client_details?.past_injuries,
      collision_limit: data?.client_details?.collision_limit,
      comprehensive_limit: data?.client_details?.comprehensive_limit,
      pip_limit: data?.client_details?.pip_limit,
      medpay_limit: data?.client_details?.medpay_limit,
    };

    // Add insurer and insurance_company - check multiple sources
    const insurerId = data?.client_details?.insurer;
    if (selectedInsurer) {
      updatedClientData.insurer = selectedInsurer.insurer_id;
      updatedClientData.insurance_company = selectedInsurer.insurer_name;
    } else if (insurerId) {
      // If insurer ID exists in form data but not in selectedInsurer, try to find from insurerOptions
      const matchedInsurer = insurerOptions?.find((opt) => opt?.insurer_id === insurerId);
      if (matchedInsurer) {
        updatedClientData.insurer = matchedInsurer?.insurer_id;
        updatedClientData.insurance_company = matchedInsurer?.insurer_name;
      }
    } else {
      // If no insurer selected and no insurerId, clear the insurer fields
      updatedClientData.insurer = '';
      updatedClientData.insurance_company = '';
    }

    const clientData = { ...caseDetails?.client_details[0], ...updatedClientData, role: undefined };

    if (!isLeadSpecific) {
      await updateCaseContactV2(caseDetails?.case_id, clientData, clientDetails?.sk);
      await getCaseDetails();
    } else {
      updateLeadDetails(clientDetails?.sk, data)
        .then(() => {
          addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.LEAD_UPDATE_SUCCESS);
          fetchLeadDetails();
        })
        .catch((error) => {
          addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, toastConstant.message.LEAD_UPDATE_FAILURE);
        });
    }
    // reset()
  };

  const loadInsurerOptions = async (inputValue, loadedOptions, additional) => {
    const { pageLimit } = additional;
    const currentPage = additional?.page !== undefined ? additional.page : 0;
    const page = currentPage + 1;

    const options = await getGlobalDropdown('', 'insurer', inputValue, caseDetails?.case_id, false, page, pageLimit, 'alphabet_ascending');

    const updatedOptions = options?.data?.data?.map((insurer) => {
      // Get claim_number directly from the insurer object
      const claim_number = insurer?.claim_number || '';

      // Get insurance limit - try multiple possible fields
      const insurance_limit = insurer?.insurance_limit || insurer?.coverage_limits?.bodily_injury || 0;

      const insuredPerson = insurer?.insured_person;
      let insuredPersonName = '';
      if (insuredPerson?.first_name || insuredPerson?.last_name) {
        insuredPersonName = `${insuredPerson?.first_name || ''} ${insuredPerson?.last_name || ''}`.trim();
      } else if (insuredPerson?.title) {
        insuredPersonName = insuredPerson.title;
      }
      const label = insuredPersonName
        ? `${insurer?.insurer_name || 'Untitled'} (${insuredPersonName})`
        : insurer?.insurer_name || 'Untitled';

      return {
        label: label,
        value: insurer?.insurer_id, // Use insurer_id as value for uniqueness
        insurer_id: insurer?.insurer_id,
        insurer_name: insurer?.insurer_name || 'Untitled',
        insurer_pk: insurer?.pk,
        insurer_sk: insurer?.sk,
        policy_number: insurer?.policy_number || '',
        claim_number: claim_number,
        insurance_limit: insurance_limit,
      };
    });

    return {
      // options: options?.data?.insurers || options?.data || [],
      options: updatedOptions || [],
      additional: {
        pageLimit: pageLimit,
        page: page,
      },
      hasMore: options?.data?.has_next,
    };
  };

  useEffect(() => {
    if (clientDetails?.dob) {
      ageCalculation(clientDetails?.dob);
    } else {
      setAge('');
    }
  }, [clientDetails?.dob]);

  const ageCalculation = (dob) => {
    if (!dob) return '';
    const DOB = parseDOB(dob);
    if (!DOB) return '';
    const today = new Date();
    const age = today.getFullYear() - DOB.getFullYear();
    setAge(age);
  };

  useEffect(() => {
    // Set initial client values (contacts are auto-loaded by hook)
    if (caseDetails?.client_details?.[0]) {
      const client = caseDetails.client_details[0];
      setSelectedContact(client);
    }
  }, [caseDetails]);

  // Map client's insurer ID when insurers are loaded
  useEffect(() => {
    const clientInsurerId = clientDetails?.insurer;
    const currentInsurerId = watch('client_details.insurer');

    // Only map if we have an insurer ID, insurers are loaded, and the dropdown is not already set
    if (clientInsurerId && insuranceProviders?.length > 0 && !currentInsurerId) {
      // Find the insurer that matches the client's insurer ID
      const matchedInsurer = insuranceProviders?.find((insurer) => insurer?.insurer_id === clientInsurerId);
      if (matchedInsurer) {
        // Set the insurer_id in the form field (dropdown value is now insurer_id)
        setValue('client_details.insurer', matchedInsurer.insurer_id);
        // Also set the selected insurer state
        setSelectedInsurer({
          label: matchedInsurer.insurer_name,
          value: matchedInsurer.insurer_id,
          insurer_id: matchedInsurer.insurer_id,
          insurer_name: matchedInsurer.insurer_name,
          insurer_pk: matchedInsurer.pk,
          insurer_sk: matchedInsurer.sk,
        });
      }
    }
  }, [insuranceProviders, clientDetails?.insurer, watch, setValue]);

  // Set selected insurer when insurerOptions or insurance_company changes
  useEffect(() => {
    const insurerId = watch('client_details.insurer');
    if (insurerId && insurerOptions.length > 0) {
      // Now matching by insurer_id since value is insurer_id
      const selected = insurerOptions?.find((opt) => opt.insurer_id === insurerId || opt.value === insurerId);
      if (selected) {
        setSelectedInsurer(selected);
      }
    }
  }, [insurerOptions, watch]);
  return (
    <>
      <div className="caption-bold my-3 d-flex justify-content-between align-items-center">
        <span>{isLeadSpecific ? 'Potential Case Details' : 'Client Information'}</span>
      </div>
      <Card className="card-bg F-size14 mb-3 info-card">
        <div className="caption-bold">
          <span>Client Information</span>
        </div>
        <Card className="card-bg F-size14 mt-2 mb-4">
          <div className="row align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12 text-muted p-medium">Full Name</div>
            <div className="col-md-8 col-12 d-flex align-items-center edit-field">
              {isFullNamedropdown && (
                <>
                  <Controller
                    name="client_details.full_name"
                    control={control}
                    render={({ field }) => (
                      <AutoComplete
                        placeholder="Search and Select Client"
                        style={{ width: '100%' }}
                        inputStyle={{ width: '100%' }}
                        value={field.value}
                        suggestions={suggestions}
                        onSelect={(e) => onSelectContact(e)}
                        completeMethod={searchContact}
                        field="label"
                        onChange={(e) => {
                          field.onChange(e.value);
                          // Update search query for debouncing
                          setSearchQuery(e.value || '');
                        }}
                        className="flex-grow-1 input-shadow"
                      />
                    )}
                  />

                  <Button
                    icon="pi pi-times"
                    className="p-button-secondary outline mx-2"
                    type="button"
                    onClick={() => {
                      setToggleFullName();
                    }}
                  />
                  <Button icon="pi pi-check" className="p-button-secondary outline" type="button" onClick={handlePopupConfirmation} />
                </>
              )}
              {!isFullNamedropdown && (
                <>
                  <Link to={`/contacts/${caseDetails?.client_details?.[0]?.contact_id || ''}`} className="text-decoration-none">
                    <div className="title-link-wrap cursor-pointer">
                      <h5 className="title-text mb-0 redirection-link-no-text-transform">
                        {`${clientDetails?.first_name} ${clientDetails?.middle_name || ''} ${clientDetails?.last_name}`
                          .replace(/\s+/g, ' ')
                          .trim()}
                      </h5>
                    </div>
                  </Link>
                  <div className="col-1 col-md-1 px-2 d-flex align-items-center ms-1 w-auto">
                    <span className="pointer">
                      <i
                        className="icon-edit"
                        onClick={() => {
                          setToggleFullName(true);
                        }}
                      ></i>
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className="row align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12 text-muted p-medium">Date of Birth</div>
            <div className="col-md-8 col-12 d-flex align-items-center edit-field">
              <span>{clientDetails?.dob ? formatDateWithoutTimezone(clientDetails?.dob) : 'N/A'}</span>
            </div>
          </div>
          <div className="row align-items-center py-2 flex-wrap">
            <div className="col-md-4 col-12 text-muted p-medium">Age</div>
            <div className="col-md-8 col-12 d-flex align-items-center edit-field">
              <span>{age}</span>
            </div>
          </div>
        </Card>
        {!caseDetails?.is_lead && (
          <>
            <div className="caption-bold mb-3 d-flex justify-content-between align-items-center">
              <span>Insurance Information</span>
              <Button
                label="Save Claim Info"
                className="btn-outline p-button-secondary py-1 px-3 "
                type="button"
                onClick={handleSubmit(handleClientInformationUpdate)}
              />
            </div>
            <Card className="card-bg F-size14 my-2">
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Vehicle Info</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.vehicle_info"
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="text"
                        className="input-shadow w-100"
                        placeholder="Year, Make, Model"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Injuries/Medical Summary</div>
                <div className="col-md-8 col-12 edit-field d-flex align-items-center">
                  <Controller
                    name="client_details.medical_summary"
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="textarea"
                        rows={16}
                        cols={30}
                        value={field.value || ''}
                        placeholder="Injuries/Medical Summary"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                        className="input-shadow w-100"
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-start py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Past Injuries</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.past_injuries"
                    control={control}
                    render={({ field }) => (
                      <TextSnippetWrapper
                        type="textarea"
                        rows={16}
                        cols={30}
                        value={field.value || ''}
                        className="input-shadow w-100"
                        placeholder="Enter Past Injuries"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Insurer</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  {/* <Controller
                name="client_details.insurance_company"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    value={field.value}
                    options={insurerOptions}
                    onChange={(e) => {
                      field.onChange(e.value);
                      // Find and store the full insurer object
                      if (e.value) {
                        const selected = insurerOptions.find((opt) => opt.value === e.value);
                        setSelectedInsurer(selected);
                      } else {
                        // Clear selected insurer when dropdown is cleared
                        setSelectedInsurer(null);
                      }
                    }}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Select Insurer"
                    className="input-shadow w-100"
                    filter
                    showClear={!!field.value}
                    filterPlaceholder="Search Insurer"
                  />
                )}
              /> */}

                  <Controller
                    name="client_details.insurer"
                    control={control}
                    render={({ field }) => {
                      const selectedOption =
                        typeof field.value === 'string' ? insurerOptions?.find((opt) => opt.value === field.value) || null : field.value;
                      return (
                        <AsyncPaginate
                          value={selectedOption}
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption?.insurer_id);
                            // Find and store the full insurer object
                            if (selectedOption) {
                              const selected = insurerOptions?.find((opt) => opt.value === selectedOption);
                              setSelectedInsurer(selected);
                            } else {
                              // Clear selected insurer when dropdown is cleared
                              setSelectedInsurer(null);
                            }
                          }}
                          loadOptions={loadInsurerOptions}
                          getOptionLabel={(option) => option?.label || option?.insurer_name}
                          getOptionValue={(option) => option?.value || option?.insurer_id}
                          additional={{
                            pageLimit: 15,
                            page: 0,
                          }}
                          debounceTimeout={300}
                          placeholder="Select Insurer"
                          className="flex-grow-1 w-75 border-0 input-shadow"
                          filter
                          isClearable={true}
                          filterPlaceholder="Search Insurer"
                          cacheUniqs={[insurerOptions]}
                          styles={userDetails?.firmDetails?.appearance_preference}
                        />
                      );
                    }}
                  />

                  <div className="col-1 col-md-1 px-1 d-flex align-items-center ms-1 w-auto">
                    <span className="pointer">
                      <i
                        className="fas fa-plus-circle add-ins "
                        onClick={() => {
                          setShowAddInsurerDialog(true);
                        }}
                      ></i>
                      <Tooltip content="Add Insurer" position="top" target=".add-ins" showDelay={500} />
                    </span>
                  </div>
                  {/* <div className="px-1 d-flex align-items-center ms-2 w-auto">
                <span className="pointer">
                  <i
                    className="fas fa-plus-circle add-insurer"
                    style={{ fontSize: '1.5rem', color: '#3b82f6' }}
                    onClick={() => setShowAddInsurerDialog(true)}
                  ></i>
                  <Tooltip content="Add New Insurer" position="top" target=".add-insurer" showDelay={500} />
                </span>
              </div> */}
                </div>
              </div>
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Collision Limit</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.collision_limit"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        className="input-shadow w-100"
                        id={field.name}
                        placeholder="Enter Amount"
                        name={field.name}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">Comprehensive Limit</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.comprehensive_limit"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        className="input-shadow w-100"
                        id={field.name}
                        placeholder="Enter Amount"
                        name={field.name}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">PIP Limit</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.pip_limit"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        className="input-shadow w-100"
                        id={field.name}
                        placeholder="Enter Amount"
                        name={field.name}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                      />
                    )}
                  />
                </div>
              </div>
              <div className="row align-items-center py-2 flex-wrap">
                <div className="col-md-4 col-12 text-muted p-medium">MedPay Limit</div>
                <div className="col-md-8 col-12 d-flex align-items-center edit-field">
                  <Controller
                    name="client_details.medpay_limit"
                    control={control}
                    render={({ field }) => (
                      <InputNumber
                        className="input-shadow w-100"
                        id={field.name}
                        placeholder="Enter Amount"
                        name={field.name}
                        mode="currency"
                        currency="USD"
                        locale="en-US"
                        {...field}
                        onChange={(e) => field.onChange(e.value)}
                      />
                    )}
                  />
                </div>
              </div>
            </Card>
          </>
        )}
      </Card>

      <UpdateContactDialog
        visible={showUpdateDialog}
        onHide={handleDialogHide}
        onRetain={handleRetain}
        onReset={handleReset}
        isDefendant={false}
      />

      {/* Add Insurance Dialog */}
      <AddInsurerDialog
        visible={showAddInsurerDialog}
        onHide={() => setShowAddInsurerDialog(false)}
        onSubmit={onSubmitInsurer}
        requiredFields={['insurance_company', 'contact_name', 'email', 'phone', 'fax', 'policy_number', 'claim_number', 'insurance_limit']}
        caseDetails={caseDetails}
        caseId={caseDetails?.case_id}
        insuranceProviders={insuranceProviders}
        excludeLawftIds={getExcludedLawftIdsForInsurer({
          selectedLawftIdList,
          insuranceProviders,
        })}
      />
    </>
  );
};

export default ClientInformation;
